import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { gettext, translationPropType } from '@eventbrite/i18n';
import { Icon } from '@eb/eds-icon';
import { ChevronRightChunky as ChevronRightChunkySvg } from '@eb/eds-iconography';
import {
    LARGE_CARD_TEMPLATE_3,
    LARGE_CARD_TEMPLATE_6,
    CARD_TEMPLATE_4,
    CARD_TEMPLATE_8,
    ARTICLE_TEMPLATE,
    CATEGORY_TEMPLATE,
    TRENDING_SEARCHES_TEMPLATE,
    LIST_CARD_TEMPLATE,
} from '../../constants';
import {
    GA_VIEW_MORE_ACTION,
    GA_CITY_BROWSE_CATEGORY,
} from '../../constants/analytics';

const HIDE_SEE_MORE_MOBILE_TEMPLATES = [
    LARGE_CARD_TEMPLATE_3,
    LARGE_CARD_TEMPLATE_6,
    CARD_TEMPLATE_4,
    CARD_TEMPLATE_8,
    ARTICLE_TEMPLATE,
    CATEGORY_TEMPLATE,
    TRENDING_SEARCHES_TEMPLATE,
    LIST_CARD_TEMPLATE,
];

const LinkedBucketHeaderTitle = ({
    linkUrl,
    trackClick,
    title,
    icon,
    seeMoreMarkedNoFollow = false,
}) => {
    return (
        <h2>
            <a
                className="eds-text-hs eds-text-color--grey-700"
                href={linkUrl}
                onClick={trackClick}
                aria-label={gettext('See more %(title)s', { title })}
                rel={seeMoreMarkedNoFollow ? 'nofollow' : null}
                data-testid="bucket-see-more"
            >
                {title}
                {icon}
            </a>
        </h2>
    );
};

const UnlinkedBucketHeaderTitle = ({ title, icon }) => {
    return (
        <h2 className="eds-text-hs eds-text-color--grey-700">
            {title}
            {icon}
        </h2>
    );
};

const SeeMore = ({
    linkUrl,
    template,
    trackClick,
    title,
    text,
    seeMoreMarkedNoFollow = false,
}) => {
    let component = null;

    if (linkUrl) {
        const seeMoreClasses = classNames(
            'see-more-link',
            'eds-link--neutral',
            'eds-text--right',
            'eds-g-cell',
            'eds-g-cell-3-12',
            {
                'eds-show-up-sm':
                    HIDE_SEE_MORE_MOBILE_TEMPLATES.indexOf(template) > -1,
            },
        );
        const anchorText = text ? text : gettext('See more');

        component = (
            <a
                className={seeMoreClasses}
                href={linkUrl}
                onClick={trackClick}
                aria-label={gettext('See more %(title)s', { title })}
                rel={seeMoreMarkedNoFollow ? 'nofollow' : null}
                data-testid="bucket-see-more"
            >
                <span>{anchorText}</span>
                <Icon type={<ChevronRightChunkySvg />} color="grey-600" />
            </a>
        );
    }

    return component;
};

export class BucketHeader extends React.PureComponent {
    static propTypes = {
        /*
         * Name of associated bucket
         */
        title: translationPropType,
        /*
         * Link to associated search to see more of similar content
         */
        linkUrl: PropTypes.string,
        /*
         * The template of the bucket being rendered
         */
        template: PropTypes.string,
        /*
         * The text of the SeeMore anchor
         */
        seeMoreText: PropTypes.string,
        /*
         * Tracking callback for see more clicks
         */
        trackSeeMore: PropTypes.func,
        /*
         * String representing user action to send to GA
         */
        gaAction: PropTypes.string,
        /*
         * Track GA action from state
         */
        trackGAEvent: PropTypes.func,
        seeMoreMarkedNoFollow: PropTypes.bool,
    };

    _trackSeeMoreClick() {
        const {
            trackSeeMore,
            gaAction,
            linkUrl,
            trackGAEvent,
            bucketKey,
        } = this.props;

        if (trackSeeMore) {
            trackSeeMore(gaAction, linkUrl);
        } else if (bucketKey) {
            trackGAEvent({
                category: GA_CITY_BROWSE_CATEGORY,
                label: bucketKey,
                action: GA_VIEW_MORE_ACTION,
            });
        }
    }

    render() {
        const {
            title,
            linkUrl,
            template,
            seeMoreText,
            iconType,
            seeMoreMarkedNoFollow,
        } = this.props;

        if (!title) {
            return null;
        }

        let icon = null;

        if (iconType) {
            icon = (
                <span className="eds-l-pad-left-2 eds-l-pad-bot-1">
                    <Icon
                        type={iconType}
                        width="36px"
                        height="36px"
                        color="grey-700"
                    />
                </span>
            );
        }

        const headerTitleClasses = classNames(
            'bucket-header__title',
            'eds-g-cell',
            'eds-g-cell-9-12',
            'eds-l-pad-bot-2',
            'eds-l-lg-pad-bot-4',
            'eds-l-lw-pad-bot-4',
            'eds-l-ln-pad-bot-4',
        );

        return (
            <div className="eds-align--center-vertical eds-l-pad-hor-3">
                <div className={headerTitleClasses}>
                    {linkUrl ? (
                        <LinkedBucketHeaderTitle
                            linkUrl={linkUrl}
                            trackClick={this._trackSeeMoreClick.bind(this)}
                            title={title}
                            icon={icon}
                            seeMoreMarkedNoFollow={seeMoreMarkedNoFollow}
                        />
                    ) : (
                        <UnlinkedBucketHeaderTitle title={title} icon={icon} />
                    )}
                </div>
                <SeeMore
                    linkUrl={linkUrl}
                    template={template}
                    trackClick={this._trackSeeMoreClick.bind(this)}
                    title={title}
                    text={seeMoreText}
                    seeMoreMarkedNoFollow={seeMoreMarkedNoFollow}
                />
            </div>
        );
    }
}

export default BucketHeader;
